import type { CourtListedPerson, LeadDetail, LeadSummary, Phone } from "@/api/models";
import { prettyDay } from "./dates.ts";

/** The engine's generic envelope addressee is not an identified petitioner. */
export function isEstateAddressee(name: string | null | undefined): boolean {
  return Boolean(name?.startsWith("Personal Representative, Estate of "));
}

/** Prefer the known petitioner; otherwise show separately sourced representatives. */
export function leadPersonName(lead: Pick<LeadSummary, "petitioner_name" | "reported_representatives">): string | null {
  if (lead.petitioner_name && !isEstateAddressee(lead.petitioner_name)) return lead.petitioner_name;
  return lead.reported_representatives.map((person) => person.name).join("; ") || null;
}

/**
 * "Court lists Patricia Mohan, not as petitioner" on a list row, short enough for its column;
 * the court's own labels ("Claimant, Minor or Representative") are in `courtListedLabels` and
 * on the lead page.
 */
export function courtListedWords(people: Pick<CourtListedPerson, "name">[]): string {
  return `Court lists ${people.map((person) => person.name).join("; ")}, not as petitioner`;
}

/** "Patricia Mohan: Claimant, Minor or Representative", one person a line, for a row's tooltip. */
export function courtListedLabels(people: Pick<CourtListedPerson, "name" | "court_role">[]): string {
  return people.map((person) => `${person.name}: ${person.court_role}`).join("\n");
}

/** A sample phone is a placeholder: never dialled, never a tel: link, never exported. */
export function isSamplePhone(phone: Phone): boolean {
  return phone.source === "sample" || !phone.dialable;
}

/**
 * A US number as people write it, "(713) 358-8962", from ten digits (or eleven with a
 * leading 1) however they arrive; anything else, an extension or a foreign number, stays
 * as given.
 */
export function formatPhone(value: string): string {
  if (!/^[\d\s().+-]+$/.test(value)) return value;
  const digits = value.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return value;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/** The tel: target of a real phone, or null for a sample or an empty value. */
export function telHref(phone: Pick<Phone, "value" | "dialable" | "source">): string | null {
  if (!phone.value || isSamplePhone(phone)) return null;
  const digits = phone.value.replace(/[^0-9+]/g, "");
  return digits ? `tel:${digits}` : null;
}

/** The app path of a lead; ids carry colons, so they are always encoded. */
export function leadHref(id: string): string {
  return `/leads/${encodeURIComponent(id)}`;
}

/** "Estate of X · Harris County · Filed 2 Jan 2026" under a lead's name; Cook's date is its claims call. */
export function filedLine(lead: Pick<LeadDetail, "deceased" | "filing_date" | "filing_date_label" | "county">): string {
  const county = lead.county.toLowerCase().includes("county") ? lead.county : `${lead.county} County`;
  return `Estate of ${lead.deceased.name.value ?? "an unnamed person"} · ${county} · ${lead.filing_date_label} ${prettyDay(lead.filing_date)}`;
}
